import React, { MouseEventHandler, useCallback, useMemo, useState } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { selectIsProjectSwitchInitiated } from '@store/actionSlices/appConfig'
import { BuildingInterface, setBuilding } from '@store/actionSlices/building'
import { selectProjectConfig } from '@store/actionSlices/projectConfig'
import { RootStateTypeExtra } from '@store/types'

import useProjectSwitchUtils from '@utilities/useProjectSwitchUtils'

import { ProjectType } from '@adUtilities/constants'
import useMapActionRouteUtils from '@adUtilities/map-action-route-utils'
import { notifyError } from '@adUtilities/notifier'
import { ApartmentRoute, DefaultRouteParamKey } from '@adUtilities/types'

import Layer from './layer'

interface LayerApartmentProps {
  building: BuildingInterface
  label: string
  actionRoute: string
}

function LayerApartment({ building, label, actionRoute }: LayerApartmentProps) {
  const dispatch = useDispatch()
  const history = useHistory()
  const { type: currentProjectType } = useSelector(selectProjectConfig)

  const { parseActionRoute } = useMapActionRouteUtils({
    currentProjectType: currentProjectType as ProjectType,
  })

  const { isValid, route, routeParams, fullpath, pathParams, projectType } =
    useMemo(
      () => parseActionRoute(actionRoute),
      [actionRoute, parseActionRoute]
    )

  const [isProjectSwitching, setIsProjectSwitching] = useState(false)
  const isRefreshing = useSelector(selectIsProjectSwitchInitiated)
  const { startProjectSwitch } = useProjectSwitchUtils()

  const hasValidProjectSwitchParam = isValid && !!routeParams.project

  const navigateTo = useMemo(
    () =>
      route === ApartmentRoute.Building || route === ApartmentRoute.LevelView
        ? fullpath
        : route,
    [fullpath, route]
  )

  const handleProjectSwitch = useCallback(async () => {
    if (!isValid) {
      return
    }
    const project = routeParams[DefaultRouteParamKey.Project]

    if (!project) {
      return
    }

    setIsProjectSwitching(true)
    const { status, message } = await startProjectSwitch({
      projectCanonicalName: project,
      redirectTo: String(route),
      redirectUrlParams: pathParams,
    })
    setIsProjectSwitching(false)

    if (!status && message) {
      notifyError(message)
    }
  }, [startProjectSwitch, route, routeParams, pathParams])

  const handleClick = useCallback<MouseEventHandler>(
    async (e) => {
      e.preventDefault()

      if (isRefreshing || isProjectSwitching || !isValid) {
        return
      }

      if (hasValidProjectSwitchParam && routeParams.project) {
        handleProjectSwitch()
        return
      }

      if (navigateTo) {
        history.push(navigateTo)
      }

      if (projectType !== ProjectType.Apartment) {
        return
      }

      dispatch(
        setBuilding({
          ...building,
          activeBlock: routeParams.block || '',
          activeLevel: routeParams.level || '',
          activeUnit: routeParams.unit || '',
        })
      )
    },
    [
      handleProjectSwitch,
      projectType,
      isValid,
      routeParams,
      navigateTo,
      hasValidProjectSwitchParam,
      isRefreshing,
      isProjectSwitching,
    ]
  )

  return (
    <Layer
      label={label}
      route={(isValid && route) || undefined}
      isValid={isValid}
      onClick={handleClick}
      isProcessing={isProjectSwitching}
      as={hasValidProjectSwitchParam ? 'button' : undefined}
    />
  )
}

export default connect(({ building }: RootStateTypeExtra) => ({
  building,
}))(LayerApartment)
